/**
 * Stop-Wache — prüft, ob eine offene Position ihren Stop verletzt hat.
 *
 * WARUM ES DIESES MODUL GIBT (Befund 08.08.2026, an echten Positionen belegt)
 *
 * Die bisherige Prüfung im Server-Cron hatte zwei Defekte, die zusammen einen
 * Phantom-Gewinner erzeugt haben:
 *
 *   1. Sie sah nur die JÜNGSTE Bar (`bars[bars.length - 1]`). Wurde der Stop
 *      an einem Dienstag gerissen und erholte sich der Kurs bis Mittwoch, war
 *      die Verletzung am Mittwoch unsichtbar — für immer.
 *   2. Sie hat nur BENACHRICHTIGT und die Position nie geschlossen.
 *
 * Der Realfall: AMZN, Einstieg 16.06.2026 zu 244,79, Stop 226,25.
 *
 *     25.06.  Eröffnung 232,02   Tief 225,55   Schluss 227,01
 *     26.06.  Eröffnung 227,21   Tief 226,12   Schluss 232,69
 *
 * Zwei Tage unter dem Stop, danach Erholung. Die Position lief weiter und
 * stand am 08.08. mit +1,60 R und +12.783 € in der Bilanz. Nach der eigenen
 * Regel wäre sie am 25.06. bei −1 R geschlossen worden. Von vier Trades wurde
 * so aus „vier Stop-Verletzungen" ein „drei Verluste und ein Gewinner" — rund
 * 20.800 € Unterschied in einer Bilanz, die die Frage beantworten sollte, ob
 * der Ansatz überhaupt etwas bringt.
 *
 * Eine Auswertung, deren Gewinner davon leben, dass der Stop nicht ausgeführt
 * wurde, misst nichts. Deshalb ist dieses Modul die Grundlage für alles
 * andere und nicht eine Verbesserung nebenbei.
 *
 * GAP-EHRLICHKEIT, gleiche Regel wie in zone-signals.ts: Eröffnet ein Tag
 * unter dem Stop, wird DORT gefüllt, nicht am (günstigeren) Stop-Kurs. Ein
 * Stop ist eine Auslöseschwelle, keine Ausführungsgarantie. Realfall SMH:
 * Der Stop lag bei 549,93, der 17.07. eröffnete bei 547,93 — gebucht wurde
 * zehn Tage später zu exakt 549,93, einem Kurs, den der Markt seitdem nicht
 * mehr hergab.
 */

import type { OhlcvBar } from '@/lib/market-data/types';

export interface StopVerletzung {
  /** Handelstag, an dem der Stop zuerst unterschritten wurde. */
  datum: string;
  /** Kurs, zu dem realistisch gefüllt worden wäre (gap-ehrlich). */
  fuellPreis: number;
  /** Tagestief dieses Tages — der Beleg. */
  tief: number;
  /** Tageseröffnung — entscheidet, ob es eine Lücke war. */
  eroeffnung: number;
  /** true, wenn der Tag schon UNTER dem Stop eröffnet hat. */
  luecke: boolean;
  /** Handelstage zwischen Verletzung und letzter vorliegender Bar. */
  tageUnbemerkt: number;
}

export interface StopWachePosition {
  /** Einstiegspreis je Stück. */
  einstieg: number;
  /** Aktueller Stop (nachgezogene Stops eingeschlossen). */
  stop: number;
  /** Einstiegstag als ISO-Datum (YYYY-MM-DD). Bars davor werden ignoriert. */
  einstiegAm: string;
}

/**
 * Sucht die ERSTE Stop-Verletzung nach dem Einstieg.
 *
 * Bewusst die erste und nicht die jüngste: Der Trade endet beim ersten Mal.
 * Alles danach ist eine Position, die es nach den eigenen Regeln nicht mehr
 * geben dürfte.
 *
 * @param bars Alle vorliegenden Tagesbars des Werts, aufsteigend. Bars am
 *             Einstiegstag selbst zählen mit — ein Stop kann noch am Kauftag
 *             reißen.
 * @returns null, wenn der Stop nie unterschritten wurde.
 */
export function findeStopVerletzung(
  pos: StopWachePosition,
  bars: OhlcvBar[]
): StopVerletzung | null {
  if (!Array.isArray(bars) || bars.length === 0) return null;
  if (!Number.isFinite(pos.stop) || pos.stop <= 0) return null;

  for (let i = 0; i < bars.length; i++) {
    const b = bars[i]!;
    if (b.timestamp < pos.einstiegAm) continue;
    if (!(b.low <= pos.stop)) continue;

    const luecke = b.open < pos.stop;
    return {
      datum: b.timestamp,
      // Lücke nach unten → dort füllen. Sonst am Stop.
      fuellPreis: luecke ? b.open : pos.stop,
      tief: b.low,
      eroeffnung: b.open,
      luecke,
      tageUnbemerkt: bars.length - 1 - i,
    };
  }
  return null;
}

/** Ergebnis eines gerissenen Stops in R und Prozent. */
export interface StopErgebnis {
  r: number;
  pct: number;
}

/**
 * Rechnet die Verletzung in R und Prozent um.
 *
 * Ohne gültiges Risiko (Stop über oder auf dem Einstieg) gibt es keine
 * R-Aussage — dann lieber null als eine erfundene Zahl.
 */
export function bewerteStopVerletzung(
  pos: StopWachePosition,
  v: StopVerletzung
): StopErgebnis | null {
  const risiko = pos.einstieg - pos.stop;
  if (!(risiko > 0)) return null;
  return {
    r: (v.fuellPreis - pos.einstieg) / risiko,
    pct: (v.fuellPreis / pos.einstieg - 1) * 100,
  };
}
